import { useEffect, useMemo, useState } from "react";
import { GitFork, Loader2, TableProperties } from "lucide-react";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { GenealogyTree } from "./GenealogyTree";
import { Badge, EmptyRow, LEVEL_LABELS, PanelTitle, Table, fmtDate } from "./ui";

export const NetworkTable = ({ network, loading, testId = "network-table", level, q }) => {
  const rows = useMemo(
    () => network.filter((n) => (level === "all" || String(n.level) === level) && (!q || `${n.name} ${n.dist_id} ${n.country}`.toLowerCase().includes(q.toLowerCase()))),
    [network, level, q]
  );
  return (
    <Table headers={["Nivel", "ID", "Nombre", "País", "Patrocinador", "Membresía", "Registro"]} testId={testId}>
      {loading && <tr><td colSpan={7} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
      {!loading && !rows.length && <EmptyRow cols={7} text="Todavía no hay personas en este nivel. Comparte tu enlace o QR." testId="network-empty" />}
      {rows.map((n, i) => (
        <tr key={n.dist_id} className="border-b border-ink/5 last:border-0" data-testid={`network-row-${i}`}>
          <td className="px-4 py-3"><span className="rounded-full bg-paper px-2.5 py-1 font-mono text-xs">N{n.level}</span></td>
          <td className="px-4 py-3 font-mono text-xs">{n.dist_id}</td>
          <td className="px-4 py-3 font-semibold">{n.name}</td>
          <td className="px-4 py-3">{n.country}</td>
          <td className="px-4 py-3 font-mono text-xs">{n.sponsor_id}</td>
          <td className="px-4 py-3"><Badge status={n.membership_status} label={n.membership_status === "activa" ? "Activa" : "Pendiente"} /></td>
          <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDate(n.created_at)}</td>
        </tr>
      ))}
    </Table>
  );
};

/** Vista completa de red: conmutador Árbol / Tabla con filtros. `root` es el distribuidor dueño de la red. */
export const NetworkView = ({ root, network: fullNetwork, loading, testId = "network" }) => {
  const [view, setView] = useState("arbol");
  const [scope, setScope] = useState("todos");
  const network = useMemo(() => (scope === "membresia" ? fullNetwork.filter((n) => n.membership_status === "activa") : fullNetwork), [fullNetwork, scope]);
  const [level, setLevel] = useState("all");
  const [q, setQ] = useState("");
  return (
    <>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="mr-2 inline-flex rounded-full border border-ink/15 bg-white p-1" data-testid={`${testId}-view-switch`}>
          {[["arbol", "Árbol", GitFork], ["tabla", "Tabla", TableProperties]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setView(v)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-ink text-white" : "hover:bg-paper"}`} data-testid={`${testId}-view-${v}`}>
              <Icon className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
        <div className="mr-2 inline-flex rounded-full border border-ink/15 bg-white p-1" data-testid={`${testId}-scope-switch`}>
          {[["todos", `Red Ganancias Mensuales (${fullNetwork.length})`], ["membresia", `Red Membresía Dorada (${fullNetwork.filter((n) => n.membership_status === "activa").length})`]].map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${scope === v ? "bg-leaf text-ink" : "hover:bg-paper"}`} data-testid={`${testId}-scope-${v}`}>{l}</button>
          ))}
        </div>
        {view === "tabla" && ["all", "1", "2", "3", "4", "5"].map((l) => (
          <button key={l} onClick={() => setLevel(l)} className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${level === l ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`network-filter-${l}`}>
            {l === "all" ? `Todos (${network.length})` : `${LEVEL_LABELS[l]} (${network.filter((n) => String(n.level) === l).length})`}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={view === "arbol" ? "Resaltar por nombre o ID" : "Buscar por nombre o ID"} className="h-9 w-full bg-white sm:ml-auto sm:w-64" data-testid="network-search" />
      </div>
      {loading ? (
        <div className="mt-6 grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : view === "arbol" ? (
        <GenealogyTree root={root} network={network} search={q} />
      ) : (
        <NetworkTable network={network} loading={false} level={level} q={q} testId={`${testId}-table`} />
      )}
    </>
  );
};

export const NetworkPanel = ({ profile }) => {
  const [network, setNetwork] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/network").then(({ data }) => setNetwork(data)).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="network-panel">
      <PanelTitle title="Mi red / Genealogía" text="Distribuidores registrados en tu red hasta 5 niveles de profundidad. Explora el árbol de forma interactiva o consulta la tabla por niveles." />
      <NetworkView root={profile} network={network} loading={loading} />
    </div>
  );
};
